const DEFAULT_BACKGROUND_FILL_VALUES = {
  backgroundWidthValue: 'auto',
  backgroundHeightValue: '105',
}

export type ParsedBackgroundFillValues = {
  backgroundWidthValue: string
  backgroundHeightValue: string
}

export type BackgroundEdits = {
  theme_id?: string | number | null
  url?: string | null
}

export type BackgroundLayout = {
  background_fill?: string
  background_top?: string | number
  background_left?: string | number
  background_opacity?: number
  background_edits?: BackgroundEdits
}

/**
 * Default background layout values used throughout the application.
 */
export const DEFAULT_BACKGROUND_LAYOUT = {
  background_fill: 'auto 105%',
  background_top: '0',
  background_left: '0',
  background_opacity: 0,
  background_edits: undefined,
}

/**
 * Convert the stored background fill value into width and height values.
 *
 * The existing editor logic stores background fill as a string such as
 * `"100% 120%"`, `"auto auto"`, or `"105%"`. The original implementation
 * removed a single percent character before splitting by space, so this helper
 * preserves that behavior to avoid subtle regressions.
 */
export const parseBackgroundFill = (
  storedBackgroundFill: unknown,
): ParsedBackgroundFillValues => {
  const storedBackgroundFillIsString = typeof storedBackgroundFill === 'string'

  if (!storedBackgroundFillIsString) {
    return { ...DEFAULT_BACKGROUND_FILL_VALUES }
  }

  const sanitizedBackgroundFill = storedBackgroundFill.replace(/%/g, '')
  const backgroundFillParts = sanitizedBackgroundFill.split(' ')

  let backgroundWidthValue = DEFAULT_BACKGROUND_FILL_VALUES.backgroundWidthValue
  let backgroundHeightValue =
    DEFAULT_BACKGROUND_FILL_VALUES.backgroundHeightValue

  const backgroundFillWidthPart = backgroundFillParts[0]
  const backgroundFillHeightPart = backgroundFillParts[1]

  const backgroundFillIncludesWidthValue =
    backgroundFillParts.length > 0 && Boolean(backgroundFillWidthPart)
  const backgroundFillIncludesHeightValue =
    backgroundFillParts.length > 1 && Boolean(backgroundFillHeightPart)

  if (backgroundFillIncludesWidthValue) {
    backgroundWidthValue = backgroundFillWidthPart
  }
  if (backgroundFillIncludesHeightValue) {
    backgroundHeightValue = backgroundFillHeightPart
  }

  return {
    backgroundWidthValue,
    backgroundHeightValue,
  }
}

/**
 * Sanitize background position values (top/left) to ensure they are valid strings.
 * Removes 'px' suffix if present and normalizes various input types.
 */
export const sanitizeBackgroundPosition = (
  value: string | number | null | undefined,
  fallback: string,
): string => {
  const valueIsMissing = value === undefined || value === null || value === ''
  if (valueIsMissing) {
    return fallback
  }

  const valueIsNumber = typeof value === 'number'
  if (valueIsNumber) {
    const normalizedNumberValue = `${value}`
    return normalizedNumberValue
  }

  const valueIsString = typeof value === 'string'
  if (valueIsString) {
    const trimmedValue = value.trim()
    const trimmedValueIsEmpty = trimmedValue === ''
    if (trimmedValueIsEmpty) {
      return fallback
    }
    const trimmedValueHasPixelSuffix = trimmedValue.endsWith('px')
    if (trimmedValueHasPixelSuffix) {
      const normalizedPixelValue = trimmedValue.slice(0, -2)
      return normalizedPixelValue
    }
    return trimmedValue
  }

  return fallback
}

/**
 * Normalize and merge background layout settings from theme and page overrides.
 * Ensures all background properties have valid values with proper fallbacks.
 */
export const normalizeBackgroundLayout = (
  themeLayout: BackgroundLayout | null | undefined,
  pageOverride: BackgroundLayout | null | undefined,
): BackgroundLayout => {
  const baseLayout = themeLayout || {}
  const overrideLayout = pageOverride || {}
  const mergedLayout = {
    ...DEFAULT_BACKGROUND_LAYOUT,
    ...baseLayout,
    ...overrideLayout,
  }

  const backgroundFill =
    mergedLayout.background_fill ||
    baseLayout.background_fill ||
    DEFAULT_BACKGROUND_LAYOUT.background_fill

  const backgroundTop = sanitizeBackgroundPosition(
    mergedLayout.background_top,
    sanitizeBackgroundPosition(
      baseLayout.background_top,
      DEFAULT_BACKGROUND_LAYOUT.background_top,
    ),
  )

  const backgroundLeft = sanitizeBackgroundPosition(
    mergedLayout.background_left,
    sanitizeBackgroundPosition(
      baseLayout.background_left,
      DEFAULT_BACKGROUND_LAYOUT.background_left,
    ),
  )

  const backgroundOpacity =
    mergedLayout.background_opacity ??
    baseLayout.background_opacity ??
    DEFAULT_BACKGROUND_LAYOUT.background_opacity

  const normalizedLayout = {
    ...mergedLayout,
    background_fill: backgroundFill,
    background_top: backgroundTop,
    background_left: backgroundLeft,
    background_opacity: backgroundOpacity,
  }

  return normalizedLayout
}
